import { inject, Injectable, Pipe, PipeTransform } from '@angular/core';
import moment from 'moment-timezone';

import { KeyValueRestrictionModel, RestrictionType, RestrictionValueType } from '@models/module/fields/enums';
import { UserStore } from '@signal-store/user.store';

@Injectable({ providedIn: 'root' })
@Pipe({
  name: 'itvDateTimeWithRestrictions',
})
export class ItvDateTimeWithRestrictionsPipe implements PipeTransform {
  userStore = inject(UserStore);
  dateCustomPipe = inject(ItvDateTimeCustomFormatPipe);

  transform(value: string | Date, restrictions: KeyValueRestrictionModel[] = [], defaultValue: string = ''): string {
    const dateFormat = this.userStore.calculatedUserSettings()?.dateFormat;
    const timeFormat = this.userStore.calculatedUserSettings()?.timeFormat;
    return this.transformValue(value, restrictions, defaultValue, dateFormat, timeFormat);
  }

  private transformValue(
    value: string | Date,
    restrictions: KeyValueRestrictionModel[],
    defaultValue: string,
    dateFormat: string,
    timeFormat: string,
  ): string {
    const datetimeFormatting = (restrictions || []).find(f => f.key == RestrictionType.DateTime);
    if (!dateFormat || !timeFormat) {
      return defaultValue;
    }

    // when formatting with restriction, only datetime restriction handles timezone
    let handleTimezone = false;

    let format = undefined;
    switch (datetimeFormatting?.value) {
      case RestrictionValueType.Date:
        format = dateFormat;
        break;
      case RestrictionValueType.Time:
        format = timeFormat;
        handleTimezone = true;
        break;
      default:
        format = `${dateFormat} ${timeFormat}`;
        handleTimezone = true;
    }

    return this.dateCustomPipe.transform(value, format, handleTimezone, defaultValue);
  }
}

@Injectable({ providedIn: 'root' })
@Pipe({
  name: 'itvDateTimeCustomFormat',
})
export class ItvDateTimeCustomFormatPipe implements PipeTransform {
  userStore = inject(UserStore);
  timeRegexp = /^(2[0-3]|[01]?[0-9]):([0-5]?[0-9])(:([0-5]?[0-9]))?(\.\d{1,3})?$/;

  transform(value: string | Date, format: string, handleTimezone: boolean = true, defaultValue: string = ''): string {
    if (!value || !format) {
      return defaultValue;
    }

    if (typeof value == 'string') {
      if (this.isTimeString(value)) {
        // If it's only a time string, we need to add a date part to make moment parse it correctly
        // because of daylight saving time, we use the current date in the user's timezone
        const datePart = moment.tz(this.userStore.userData()?.ianaTimeZoneId).format('YYYY-MM-DD');
        value = `${datePart}T${value}Z`;
      }
      const hasTimezone = value.endsWith('Z');
      if (handleTimezone && !hasTimezone) {
        value = value + 'Z';
      } else if (!handleTimezone && hasTimezone) {
        value = value.slice(0, -1);
      }
    }

    const ianaTimeZoneId = !handleTimezone ? undefined : this.userStore.userData()?.ianaTimeZoneId;
    if (ianaTimeZoneId) {
      return moment.utc(value).tz(ianaTimeZoneId).format(format);
    }
    return moment.utc(value).utcOffset(0).format(format);
  }

  private isTimeString(str: string) {
    return this.timeRegexp.test(str);
  }
}

@Injectable({ providedIn: 'root' })
@Pipe({
  name: 'itvDate',
})
export class ItvDatePipe implements PipeTransform {
  userStore = inject(UserStore);
  dateCustomPipe = inject(ItvDateTimeCustomFormatPipe);

  transform(value: string | Date, defaultValue: string = ''): string {
    const dateFormat = this.userStore.calculatedUserSettings()?.dateFormat;

    if (!dateFormat) {
      return defaultValue;
    }

    return this.dateCustomPipe.transform(value, dateFormat, false, defaultValue);
  }
}

@Injectable({ providedIn: 'root' })
@Pipe({
  name: 'itvTime',
})
export class ItvTimePipe implements PipeTransform {
  userStore = inject(UserStore);
  dateCustomPipe = inject(ItvDateTimeCustomFormatPipe);

  transform(value: string | Date, defaultValue: string = ''): string {
    const timeFormat = this.userStore.calculatedUserSettings()?.timeFormat;

    if (!timeFormat) {
      return defaultValue;
    }

    return this.dateCustomPipe.transform(value, timeFormat, true, defaultValue);
  }
}

@Injectable({ providedIn: 'root' })
@Pipe({
  name: 'itvDateTime',
})
export class ItvDateTimePipe implements PipeTransform {
  userStore = inject(UserStore);
  dateCustomPipe = inject(ItvDateTimeCustomFormatPipe);

  transform(value: string | Date, defaultValue: string = ''): string {
    const datetimeFormat = this.userStore.calculatedUserSettings()?.datetimeFormat;

    if (!datetimeFormat) {
      return defaultValue;
    }

    return this.dateCustomPipe.transform(value, datetimeFormat, true, defaultValue);
  }
}

@Injectable({ providedIn: 'root' })
@Pipe({
  name: 'itvFromNow',
})
export class ItvFromNowPipe implements PipeTransform {
  userStore = inject(UserStore);

  transform(value: string, handleTimezone = true): string {
    let val = value;
    if (handleTimezone && !value.endsWith('Z')) {
      val = value + 'Z';
    }

    const ianaTimeZoneId = handleTimezone ? this.userStore.userData()?.ianaTimeZoneId : undefined;

    // Parse moment with timezone or UTC
    const m = ianaTimeZoneId ? moment.utc(val).tz(ianaTimeZoneId) : moment.utc(val).utcOffset(0);

    const diffMinutes = moment().diff(m, 'minutes');
    if (diffMinutes < 60) {
      return 'general.lastHour';
    }

    return m.fromNow();
  }
}
